import urlJoin from 'url-join'
import createFetchival from '@exodus/fetch/create-fetchival'
import { create } from 'superstruct'
import { FILTER_LIST_MODES, ORDER_TYPES, PAYMENT_METHOD_TYPES, PROVIDERS } from '../constants.js'
import { createPaymentMethodValidator } from '../payment-method.js'
import { ERROR_PAYMENT_METHOD_NOT_SUPPORTED } from '../errors.js'
import { Quote } from '../schema/quote.js'
import { BuyOrder } from '../schema/order.js'
import { filterByCountries } from '../utils.js'
import { APP_URL, PAYMENT_METHODS } from './constants.js'

export * from './constants.js'
export * from './utils.js'

const xopayProvider = ({
  countries = Object.create(null),
  countriesMode = FILTER_LIST_MODES.blacklist,
  enabled = true,
  url = APP_URL,
  fetch: globalFetch = fetch.bind(globalThis), // eslint-disable-line no-undef
  paymentMethodTypes,
} = {}) => {
  const fetchival = createFetchival({ fetch: globalFetch })
  const apiUrl = urlJoin(url, 'api')
  const fetch_ = fetchival(apiUrl)

  const getAssets = async () => {
    const assets = await fetch_('v1/assets').get({ provider: 'exopay' })

    return assets.map(({ providers, ...asset }) => ({
      ...asset,
      ...providers.exopay,
    }))
  }

  const getCountries = async ({ countryCode, regionCode } = Object.create(null)) => {
    const countries_ = []

    for (const { code, providers, ...country } of await fetch_('v1/countries').get({
      provider: 'exopay',
    })) {
      const isCountrySupported = !filterByCountries(
        countries,
        code === countryCode ? [countryCode, regionCode] : code,
        {
          mode: countriesMode,
          properties: [ORDER_TYPES.buy],
        }
      )

      if (!isCountrySupported) {
        continue
      }

      countries_.push({
        ...country,
        ...providers.exopay,
        code,
      })
    }

    return countries_
  }

  const getOrder = async (id) => {
    const encodedId = encodeURIComponent(id)
    const {
      createdAt,
      extraFee,
      fee,
      from,
      networkFee,
      paymentMethod,
      rate,
      status,
      to,
      totalFee,
      txId,
      updatedAt,
      walletAddress,
    } = await fetch_(`v1/orders/${encodedId}`).get({
      provider: 'exopay',
    })

    const type = PAYMENT_METHODS.get(paymentMethod)?.type
    const paymentMethodType = Array.isArray(type) ? type[0] : type

    return create(
      {
        createdAt: new Date(createdAt),
        extraFee,
        fee,
        from,
        id,
        networkFee,
        paymentMethod,
        paymentMethodType,
        provider: PROVIDERS.xopay,
        providerId: id,
        rate,
        status,
        to,
        totalFee,
        txId,
        updatedAt: new Date(updatedAt),
        walletAddress,
      },
      BuyOrder
    )
  }

  const getQuote = async ({
    countryCode,
    fromAmount,
    fromAsset,
    paymentMethod = PAYMENT_METHOD_TYPES.card,
    toAsset,
    type = ORDER_TYPES.buy,
  } = {}) => {
    const providerPaymentMethod = getPaymentMethod(paymentMethod, {
      country: countryCode,
      currency: fromAsset,
    })

    if (type !== ORDER_TYPES.buy) {
      throw new Error('Only `buy` orders are accepted')
    }

    if (!providerPaymentMethod) {
      throw new Error(ERROR_PAYMENT_METHOD_NOT_SUPPORTED.message)
    }

    const quote = await fetch_('v1/quotes').get({
      fromAmount,
      fromAsset,
      toAsset,
      provider: 'exopay',
    })

    return create(
      {
        ...quote,
        paymentMethod: providerPaymentMethod,
        provider: PROVIDERS.xopay,
        type,
      },
      Quote
    )
  }

  const getPaymentMethod = (paymentMethod, parameters = {}) => {
    const providerPaymentMethod = [...PAYMENT_METHODS.values()].find(
      createPaymentMethodValidator({ ...parameters, type: paymentMethod, paymentMethodTypes })
    )

    return providerPaymentMethod?.id
  }

  return {
    name: PROVIDERS.xopay,
    enabled,
    getAssets,
    getCountries,
    getOrder,
    getPaymentMethod,
    getQuote,
    orderTypes: [ORDER_TYPES.buy],
  }
}

export default xopayProvider
